import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { ICreateIncome, IIncomeResponse, IUpdateIncome } from './types';

export const useGetLabelIncomes = (labelId: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IIncomeResponse[], DefaultError>({
    queryKey: ['label-incomes', labelId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Income/Label`,
        params: { labelId },
      });

      return responseHandler(response, {
        groupName: `useGetLabelIncomes - ${labelId}`,
        notFoundPath: '/income',
      });
    },
    enabled: enabled,
  });
};

export const useGetIncome = (id: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IIncomeResponse, DefaultError>({
    queryKey: ['income', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Income/${id}`,
      });

      return responseHandler(response, {
        groupName: `usetGetIncome - ${id}`,
        notFoundPath: '/income',
      });
    },
    enabled: enabled,
  });
};

export const usePostIncome = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostIncome');
  return useMutation<string, DefaultError, ICreateIncome>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('income.what'),
        })
      );

      const response = await apiClient({ method: 'POST', url: 'Income', body });

      return responseHandler(response, {
        groupName: 'usePostIncome',
        notFoundPath: '/income',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('income.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutIncome = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutIncome');

  return useMutation<boolean, DefaultError, IUpdateIncome>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('income.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'Income',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutIncome',
        notFoundPath: '/income',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('income.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutIncomeFile = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutIncomeFile');

  return useMutation<boolean, DefaultError, { file: File; id: string }>({
    mutationFn: async ({ file, id }) => {
      loading(
        i18next.t('service-message.add.loading', {
          what: i18next.t('track.file-what'),
        })
      );

      const formData = new FormData();

      formData.append('SettlementIncomeId', id);
      formData.append('File', file);

      const response = await apiClient({
        method: 'PUT',
        url: 'Income/File',
        body: formData,
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'usePutIncomeFile',
        notFoundPath: '/income',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.add.success', {
          what: i18next.t('track.file-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteIncome = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteIncome');

  return useMutation<boolean, DefaultError, string>({
    mutationFn: async id => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('income.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Income`,
        body: { settlementIncomeId: id },
      });

      return responseHandler(response, {
        groupName: 'useDeleteIncome',
        notFoundPath: '/income',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('income.what'),
        })
      );
    },
    onError: dismiss,
  });
};
